import { Context, Controller } from '@hotwired/stimulus';
import type { Editor } from '@tiptap/core';
import { toggleHidden } from 'src/utils/dom-toggle';
import { emitter } from 'src/emitter';

const DEBOUNCE_UPDATES = 500;

function extractValue(editor: Editor): string {
  const blocks = editor.getJSON().content;
  if (!blocks || blocks.length === 0) {
    return '';
  }

  return (blocks[0].content || [])
    .filter((item) => item.type === 'text')
    .map((item) => item.text || '')
    .join('\n');
}

export default class TipTapCodeController extends Controller {
  public static targets = ['editor', 'input', 'fallback'];
  public static values = {
    readonly: Boolean,
    interactive: Boolean,
    language: String,
    scriptLineWakeword: String,
  };

  declare private hasFallbackTarget: boolean;
  declare private hasEditorTarget: boolean;
  declare private hasInputTarget: boolean;

  declare private fallbackTarget: HTMLTextAreaElement;
  declare private editorTarget: HTMLElement;
  declare private inputTarget: HTMLInputElement;

  declare private hasReadonlyValue: boolean;
  declare private hasInteractiveValue: boolean;
  declare private hasLanguageValue: boolean;
  declare private hasScriptLineWakewordValue: boolean;

  declare private readonlyValue: boolean;
  declare private interactiveValue: boolean;
  declare private languageValue: string;
  declare private scriptLineWakewordValue: boolean;

  private editor!: Editor;
  private fallback!: HTMLTextAreaElement;
  private input!: HTMLInputElement;
  private toolbar!: HTMLDivElement;
  private debounce: boolean = true;

  private onUpdateTimer!: ReturnType<typeof setTimeout>;
  private connected: boolean = false;
  private inflated: boolean = false;

  constructor(context: Context) {
    super(context);

    this.onUpdate = this.onUpdate.bind(this);
    this.onRequestSubmit = this.onRequestSubmit.bind(this);
    this.onGotoScriptLine = this.onGotoScriptLine.bind(this);
  }

  connect(): void {
    if (!this.hasEditorTarget) {
      throw new Error('Mark-up for rich text is incorrect.');
    }

    this.connected = true;

    if (this.hasInputTarget) {
      this.input = this.inputTarget!;
    } else {
      this.input = document.createElement('input');
      this.input.type = 'hidden';
      this.input.name = 'content';
      this.input.value = this.editorTarget.innerHTML || '';

      if (this.hasEditorTarget) {
        this.element.append(this.input);
      } else {
        this.element.parentElement!.append(this.input);
      }
    }

    console.debug('[tiptap] connect', this.input.value);

    let placeholder = 'Write something...';
    if (this.hasFallbackTarget) {
      this.fallback = this.fallbackTarget;
      this.fallbackTarget.remove();

      placeholder = this.fallback.placeholder;

      this.input.disabled = false;
    }

    void Promise.all([
      import('src/tiptap/code_editor').then(
        ({ default: makeCodeEditor }) => makeCodeEditor,
      ),
    ]).then(([makeCodeEditor]) => {
      if (!this.connected) {
        return;
      }

      this.editor = makeCodeEditor(this.editorTarget, {
        content: '; loading',
        placeholder,
        callbacks: {
          onCreated: (editor: Editor) => {
            const initialValue = this.input.value;
            // const nextValue = extractValue(editor);
            console.debug('[tiptap] created');
            editor.commands.clearContent(false);
            requestAnimationFrame(() => {
              if (initialValue) {
                editor.commands.setContent(
                  {
                    type: 'doc',
                    content: [
                      {
                        type: 'text',
                        text: initialValue,
                      },
                    ],
                  },
                  // `<code>${initialValue}${initialValue}</code>`,
                  // false,
                );
              }
            });
          },
          onUpdate: this.onUpdate,
        },
        options: {
          editable: this.hasReadonlyValue ? !this.readonlyValue : true,
          defaultLanguage: this.hasLanguageValue ? this.languageValue : 'lisp',
        },
      });

      this.inflate(this.editor);

      this.element
        .closest('form')
        ?.querySelectorAll('input[type="submit"], button[type="submit"]')
        .forEach((submitted) => {
          if (!submitted.hasAttribute('formaction')) {
            // Invoke after other attachments
            // submitted.setAttribute('type', 'button');
            // setTimeout(() => {
            submitted.addEventListener('click', this.onRequestSubmit);
            // }, 1);
          }
        });

      emitter.on('script:line:go', this.onGotoScriptLine);
    });
  }

  disconnect(): void {
    this.connected = false;

    if (this.inflated) {
      this.editor.destroy();
      this.inflated = false;
    }

    if (this.fallback) {
      this.element.append(this.fallback);
      this.input.disabled = true;
    }

    if (!this.hasInputTarget) {
      this.input.remove();
    }

    this.toolbar.remove();

    this.element
      .closest('form')
      ?.querySelectorAll('input[type="submit"], button[type="submit"]')
      .forEach((submitted) =>
        submitted.removeEventListener('click', this.onRequestSubmit),
      );

    emitter.off('script:line:go', this.onGotoScriptLine);
  }

  private inflate(editor: Editor) {
    const buttons = [
      button('undo action')
        .onClick((editor) => editor.chain().focus().undo().run())
        .build(editor),
      button('redo action')
        .onClick((editor) => editor.chain().focus().redo().run())
        .build(editor),
    ];

    // TODO: https://www.w3.org/WAI/ARIA/apg/patterns/toolbar/examples/toolbar/

    this.toolbar = document.createElement('div');
    this.toolbar.classList.add(
      'tiptap-toolbar',
      'group',
      'print:hidden',
      'not-prose',
    );
    this.toolbar.setAttribute('role', 'toolbar');
    this.toolbar.setAttribute('aria-controls', this.editorTarget.id);
    this.toolbar.append(...buttons);

    if (this.hasReadonlyValue && this.readonlyValue) {
      toggleHidden(this.toolbar, true);
    }

    this.element.prepend(this.toolbar);
    this.inflated = true;
  }

  private onUpdate(_props: unknown): void {
    clearTimeout(this.onUpdateTimer);

    // Debounce updates
    this.onUpdateTimer = setTimeout(() => {
      this.input.value = extractValue(this.editor);
    }, DEBOUNCE_UPDATES);
  }

  private onRequestSubmit(event: Event): void {
    if (!this.debounce) {
      this.debounce = true;
      return;
    }

    // Force update
    this.editor.commands.blur();

    if (event.target instanceof Element) {
      const submitter = event.target.closest(
        'button:not([type="button"]), input[type="submit"]',
      ) as HTMLElement;
      if (submitter) {
        submitter.focus();
      }
    }

    clearTimeout(this.onUpdateTimer);

    // Grab new content
    this.input.value = extractValue(this.editor);

    /**
     * The following code was present to ensure the input.value had the latest
     * values, but it seems to be possible to trigger this behaviour by doing an
     * editor blur followed by a grab HTML.
     */

    /*
    event.preventDefault();

    const input = event.currentTarget as HTMLInputElement | HTMLButtonElement;
    input.disabled = true;

    clearTimeout(this.onUpdateTimer as any);

    this.onUpdateTimer = setTimeout(() => {
      input.disabled = false;
      this.input.value = this.editor.getHTML();

      if (
        requestSubmit(
          input.form,
          event.currentTarget as HTMLInputElement | HTMLButtonElement,
        )
      ) {
        this.debounce = false;
      }
    }, DEBOUNCE_UPDATES + 1);
    */
  }

  private onGotoScriptLine(params: unknown): void {
    if (!this.hasScriptLineWakewordValue) {
      return;
    }

    if (!params || typeof params !== 'object') {
      return;
    }

    if (
      !('for' in params) ||
      params.for !== this.scriptLineWakewordValue ||
      !('n' in params) ||
      typeof params.n !== 'number'
    ) {
      return;
    }

    const position =
      params.n <= 1
        ? 0
        : extractValue(this.editor)
            .split('\n')
            .slice(0, params.n - 1)
            .reduce((sum, line) => sum + line.length + 1, 1);

    console.log({ position });
    this.editor.commands.focus(position);
    requestAnimationFrame(() => {
      this.editor.commands.setTextSelection(position);
    });
  }
}

function button(label: string) {
  return new ButtonBuilder().label(label);
}

class ButtonBuilder {
  private labelValue: undefined | string;
  private iconPathValue: undefined | string;
  private iconViewBox: undefined | string;
  private iconFill: undefined | boolean;
  private iconStroke: undefined | boolean;
  private additionClassNames: undefined | string[];
  private isActiveValue: undefined | ((editor: Editor) => boolean);
  private isDisabledValue: undefined | ((editor: Editor) => boolean);
  private onClickValue:
    undefined | ((editor: Editor, button: HTMLButtonElement) => void);
  private visibility: undefined | 'always' | 'formatting';

  constructor() {}

  public path(
    next: string,
    viewBox = '0 0 15 15',
    fill = true,
    stroke = false,
  ) {
    this.iconPathValue = next;
    this.iconViewBox = viewBox;
    this.iconFill = fill;
    this.iconStroke = stroke;

    return this;
  }

  public label(next: string) {
    this.labelValue = next;
    return this;
  }

  public isActive(next: undefined | ((editor: Editor) => boolean)) {
    this.isActiveValue = next;
    return this;
  }

  public isDisabled(next: undefined | ((editor: Editor) => boolean)) {
    this.isDisabledValue = next;
    return this;
  }

  public classList(...params: string[]) {
    this.additionClassNames = params;
    return this;
  }

  public onClick(
    next: undefined | ((editor: Editor, button: HTMLButtonElement) => void),
  ) {
    this.onClickValue = next;
    return this;
  }

  public setVisibility(visibility: 'always' | 'formatting') {
    this.visibility = visibility;
    return this;
  }

  public build(editor: Editor) {
    const node = document.createElement('button');
    node.type = 'button';
    node.textContent = this.labelValue || '(button)';
    node.disabled = !this.onClickValue;
    node.classList.add(
      'button',
      '--xs',
      '--input',
      'group',
      ...(this.additionClassNames ?? []),
    );

    if (this.iconPathValue && this.iconViewBox) {
      const figure = document.createElement('figure');
      figure.classList.add('flex', 'items-center');

      const icon = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'svg',
      );
      icon.setAttributeNS(null, 'viewBox', this.iconViewBox);
      icon.setAttributeNS(null, 'width', '15');
      icon.setAttributeNS(null, 'height', '15');
      icon.setAttributeNS(null, 'fill', 'none');

      const iconPath = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'path',
      );
      iconPath.setAttributeNS(null, 'd', this.iconPathValue);
      iconPath.setAttributeNS(
        null,
        'fill',
        this.iconFill ? 'currentColor' : 'none',
      );
      iconPath.setAttributeNS(
        null,
        'stroke',
        this.iconStroke ? 'currentColor' : 'none',
      );
      iconPath.setAttributeNS(null, 'fill-rule', 'evenodd');
      iconPath.setAttributeNS(null, 'clip-rule', 'evenodd');

      icon.appendChild(iconPath);

      const figcaption = document.createElement('figcaption');
      figcaption.textContent = node.textContent;
      figcaption.classList.add(
        'sr-only',
        'sm:not-sr-only',
        'sm:pl-2',
        // 'group-focus-visible:not-sr-only',
        // 'group-focus-visible:ml-2'
      );

      figure.append(icon, figcaption);

      node.textContent = '';
      node.append(figure);
    }

    if (this.visibility === 'formatting') {
      node.classList.add('--formatting');
    }

    if (this.isActiveValue) {
      const isActiveValueFn = this.isActiveValue;

      let debounceTimer: ReturnType<typeof setTimeout>;
      const onUpdate = () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(refresh, 150);
      };

      const refresh = () => {
        const next = isActiveValueFn(editor);
        node.classList.toggle('is-active', next);
        node.classList.toggle('--pressed', next);

        node.setAttribute('aria-pressed', String(next));

        // TODO: isDisabledValue
      };

      editor.on('selectionUpdate', onUpdate);
      editor.on('update', onUpdate);
    }

    if (this.onClickValue) {
      const onClickValueFn = this.onClickValue;
      node.addEventListener('click', () => onClickValueFn(editor, node));
    }

    return node;
  }
}
